import React, { useEffect, useRef, useState } from 'react';
import DataNotFound from '../../../../components/dataNotFound';
import PageLoader from '../../../../../component/PageLoader';
import AccountListTable from '../components/accountListTable';
import { useSelector } from 'react-redux';
import { getParentAccounts } from '../../../../../services/settings/accounting';
import CreateParentAccount from '../components/createParentAccount';
import DeleteParentAccountModal from '../components/deleteParentAccountModal';
import SettingHeaderComponent from '../../components/settingHeaderComponent';
import ParentAccountDetail from './parentAccountDetails';
import { hasPermission } from '../../../../../utils/controller';
import AccountingDropdownComponent from '../components/accountingDropdownComponent';
import { PARENT_ACCOUNT_STATUS_LIST } from '../../../../../utils/constant';
import SearchAccountsComponent from '../components/searchAccountsComponent';
import Pagination from '../../../../../component/pagination';
import { useSearchParams } from 'react-router-dom';

export default function ParentAccounts({ activeTab }) {
	const [searchParams, setSearchParams] = useSearchParams();
	const [isOpenAccountModal, setIsOpenAccountsModal] = useState(false);
	const [isParentAccountOpenDropdown, setIsParentAccountOpenDropdown] = useState(false);
	const [isOpenDeleteAccountModal, setIsOpenDeleteAccountModal] = useState(false);
	const [loader, setLoader] = useState(false);
	const isFirstRender = useRef(true);
	const [parentAccount, setParentAccount] = useState({
		list: [],
		totalPages: 0,
	});
	const user = useSelector((state) => state?.userInfo?.userInfo?.data);
	const [sortDirection, setSortDirection] = useState({ id: 'asc' });
	const [parentAccountId, setParentAccountId] = useState(null);
	const [type, setType] = useState('parent-account-list');
	const [searchText, setSearchText] = useState('');
	const [params, setParams] = useState({
		page: Number(searchParams.get('page')) || 1,
		pageSize: Number(searchParams.get('pageSize')) || 20,
		order_by: searchParams.get('order_by') || 'created_at',
		sort_by: searchParams.get('sort_by') || 'desc',
		search: searchParams.get('search') || '',
		status: searchParams.get('status') || '',
	});

	const sortDataList = (param) => {
		const newDirection = sortDirection[param.order_by] === 'desc' ? 'asc' : 'desc';
		setSortDirection({ [param.order_by]: newDirection });

		setParams((prev) => ({
			...prev,
			order_by: param.order_by,
			sort_by: newDirection,
		}));
	};

	const onRowClick = (id) => {
		if (id && hasPermission('settings.parent_accounts.view', user?.role?.role_permissions)) {
			setParentAccountId(id);
			setType('parent-account-details');
		}
	};

	const toggle = () => {
		setIsParentAccountOpenDropdown(!isParentAccountOpenDropdown);
	};

	const getParentAccountsList = async () => {
		try {
			setLoader(true);
			const { accounts, total_pages } = await getParentAccounts(params);
			if (accounts.length > 0) {
				const data = mappedTableData(accounts);
				setParentAccount((prev) => ({
					...prev,
					list: data,
					totalPages: total_pages,
				}));
			} else {
				setParentAccount((prev) => ({ ...prev, list: [] }));
			}
		} catch (error) {
		} finally {
			setLoader(false);
		}
	};

	function mappedTableData(data) {
		return data?.map((ele) => {
			return {
				id: ele.account_id,
				parent_account_name: ele.account_name || 'N/A',
				sub_account_count: ele?.sub_account_count || '0',
				status: ele?.status,
				description: ele?.description || '',
			};
		});
	}

	const columns = [
		{ key: 'parent_account_name', label: 'PARENT ACCOUNT NAME' },
		{ key: 'sub_account_count', label: 'INCLUDED ACCOUNTS' },
		{ key: 'status', label: 'STATUS' },
		{ key: 'description', label: 'DESCRIPTION' },
		{ key: 'action', label: 'ACTIONS' },
	];

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return; // ⛔ skip first render
		}
		const debounceTimer = setTimeout(() => {
			setParams((prev) => ({
				...prev,
				page: 1,
				search: searchText,
			}));
		}, 500); // ⏱ debounce delay (ms)

		return () => clearTimeout(debounceTimer);
	}, [searchText]);

	const setFilterFromUrl = () => {
		setSortDirection({
			[searchParams.get('order_by')]: searchParams.get('sort_by') || '',
		});
		setType(searchParams.get('type') || 'parent-account-list');
		if (searchParams.get('type') === 'parent-account-list') {
			setSearchText(searchParams.get('search') || '');
		}
	};

	useEffect(() => {
		setFilterFromUrl();
	}, []);

	const setUrlSearchParams = () => {
		const url = new URLSearchParams();
		url.set('type', type);
		if (type === 'parent-account-list') {
			url.set('page', params.page);
			url.set('pageSize', params.pageSize);
			url.set('order_by', params.order_by);
			url.set('sort_by', params.sort_by);
			url.set('search', params.search);
			url.set('status', params.status);
		}

		setSearchParams(url);
	};

	useEffect(() => {
		setUrlSearchParams();
	}, [params, type]);

	useEffect(() => {
		if (searchParams.get('type') === 'parent-account-list') {
			getParentAccountsList();
		}
	}, [searchParams]);

	return (
		<div>
			{type === 'parent-account-list' ? (
				<div>
					<SettingHeaderComponent
						title={activeTab}
						subtitle={
							'Manage and organize parent accounts used for account grouping and financial hierarchy.'
						}
						buttons={[
							{
								disabled: false,
								className: 'btn-primary',
								add: true,
								close: false,
								dataTestId: 'create-parent-account-button',
								button: 'New Parent Account',
								handler: () => {
									setIsOpenAccountsModal(true);
									setParentAccountId(null);
								},
							},
						]}
					/>
					<div className="d-flex px-3 py-4 gap-4">
						<AccountingDropdownComponent
							params={params}
							toggle={toggle}
							type={'Parent Account'}
							list={PARENT_ACCOUNT_STATUS_LIST}
							isAccountOpenDropdown={isParentAccountOpenDropdown}
							setParams={setParams}
						/>
						<SearchAccountsComponent
							searchText={searchText}
							setSearchText={setSearchText}
							placeholder={'Search by parent account name'}
						/>
					</div>
					{loader ? (
						<PageLoader />
					) : parentAccount.list.length > 0 ? (
						<div className="productTable-body">
							<AccountListTable
								className={'parent-account-list-table-container'}
								columns={columns}
								accountType={'parent-accounts'}
								row={parentAccount.list}
								sortDataList={sortDataList}
								onRowClick={onRowClick}
								setAccountId={setParentAccountId}
								sortDirection={sortDirection}
								setIsOpenAccountsModal={setIsOpenAccountsModal}
								setIsOpenDeactivateConfirmAccountModal={setIsOpenDeleteAccountModal}
							/>
						</div>
					) : (
						<DataNotFound
							title={'No parent accounts added yet!'}
							subtitle={'Manage and organize parent accounts to structure '}
							content={'related financial accounts efficiently.'}
							descriptionTitle={''}
							descriptionSubTitle={''}
							descriptionContent={''}
							buttonText={'Add New Account'}
							disabled={false}
							onClick={() => setIsOpenAccountsModal(true)}
						/>
					)}
					{parentAccount.list.length > 0 && (
						<Pagination
							PageFix={1}
							currentPage={params.page}
							totalPages={parentAccount.totalPages}
							onChangePage={(page) => {
								setParams((prev) => ({
									...prev,
									page: page,
								}));
							}}
							pageSize={params.pageSize}
							setParam={(value) => {
								setParams((prev) => ({
									...prev,
									pageSize: value,
									page: 1,
								}));
							}}
						/>
					)}
				</div>
			) : type === 'parent-account-details' ? (
				<ParentAccountDetail
					parentAccountId={parentAccountId}
					type={type}
					setType={setType}
					setParentAccountId={setParentAccountId}
				/>
			) : (
				<></>
			)}
			{isOpenAccountModal && (
				<CreateParentAccount
					isOpenModal={isOpenAccountModal}
					setIsOpenModal={setIsOpenAccountsModal}
					id={parentAccountId}
					getList={getParentAccountsList}
				/>
			)}
			{isOpenDeleteAccountModal && (
				<DeleteParentAccountModal
					isOpenModal={isOpenDeleteAccountModal}
					setIsOpenModal={setIsOpenDeleteAccountModal}
					parentAccountId={parentAccountId}
					getList={getParentAccountsList}
				/>
			)}
		</div>
	);
}
